/**
 * Bounded personal-evidence conversion and conservative prediction application. Confidence values
 * express evidence support rather than calibrated probability, and predictions cannot replace
 * explicit user-entered planning values.
 *
 * @module apps/web/src/lib/browserInferenceModel
 */

import type { TaskStateProfile, TaskUI } from '@fluxability/types';
import { runChunked, type ChunkOptions } from './compute/mainThread';

/**
 * Worker prediction batch with optional per-output confidence, aggregate evidence counts,
 * backend/model/schema telemetry, and a final-server-rerank flag.
 */
export interface InferenceBatch {
  /** Per-activity suggestions produced by the bounded browser inference batch. */
  predictions: Array<{
    /** Activity identifier used to join the suggestion back to current local state. */
    activity_id: string;
    /** Predicted duration in minutes; application validates integer bounds before using it. */
    duration_minutes: number;
    /** Predicted productivity investment on the 0–10 scale. */
    required_productivity_energy: number;
    /** Predicted rejuvenation investment on the 0–10 scale. */
    required_rejuvenation_energy: number;
    /** Predicted productivity return on the 0–10 scale. */
    productivity_return: number;
    /** Predicted rejuvenation return on the 0–10 scale. */
    rejuvenation_return: number;
    /** Predicted emotion label; the current patch builder does not apply it. */
    emotion: string;
    /** Aggregate evidence-support score, not a calibrated correctness probability. */
    confidence: number;
    /** Number of supporting observations reported by inference. */
    evidence_count: number;
    /** Per-output support scores used to decide whether individual missing values may be filled. */
    output_confidence?: number[];
  }>;
  /** Execution backend, evidence support, versions, and fallback diagnostics. */
  telemetry: {
    /** Actual inference path: WebGPU, CPU WASM, or server fallback. */
    backend: 'webgpu' | 'wasm_cpu' | 'server_fallback';
    /** Measured batch inference duration in milliseconds. */
    elapsed_millis: number;
    /** Aggregate evidence-support score, not a calibrated correctness probability. */
    confidence: number;
    /** Number of supporting observations reported by inference. */
    evidence_count: number;
    /** Capability description returned by the browser inference path. */
    webgpu_capability: string;
    /** Version identifier for the inference implementation or model. */
    model_version: string;
    /** Version of the feature representation used for this batch. */
    feature_schema_version: string;
    /** Whether server-side constraint evaluation must still rank final placements. */
    final_server_rerank_required: boolean;
    /** Reason the preferred backend could not be used, when supplied. */
    fallback_reason?: string;
  };
}
/** Accept only a finite JavaScript number as model evidence; numeric strings and other input types remain missing. */
const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
/** Accept a finite numeric score only inside 0–10; out-of-range or nonnumeric values remain missing evidence. */
const score = (value: unknown): number | null => {
  const number = finite(value);
  return number !== null && number >= 0 && number <= 10 ? number : null;
};
/** Normalize descriptive text into bounded lowercase keyword features used for local similarity; these terms are features, not identity or authorization evidence. */
const terms = (value: string) =>
  value
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(
      term =>
        Boolean(term) &&
        !['personal', 'activity', 'task', 'the', 'a', 'an', 'and', 'to', 'of'].includes(term)
    )
    .slice(0, 32)
    .map(term => term.slice(0, 64));
/**
 * One feature of an exported evidence row. The export is unchecked JSON, so every field is
 * `unknown` until `observationFromRow` validates it.
 */
interface EvidenceFeature {
  /** Feature name; rows without one are dropped. */
  key?: unknown;
  /** `personal` for features the personal model may use; anything else is dropped. */
  scope?: unknown;
  /** Team that owns a team feature; any value excludes the feature. */
  teamId?: unknown;
  /** Feature weight, clamped to 0–100 when finite. */
  weight?: unknown;
  /** Feature specificity, clamped to 0–10 when finite. */
  specificity?: unknown;
  /** Tagged value: `numerical` with `normalized`, or `categorical` with `value`. */
  value?: {
    /** `numerical` or `categorical`; other kinds drop the feature. */
    kind?: unknown;
    /** Numerical value, clamped to 0–1 when finite. */
    normalized?: unknown;
    /** Categorical label, trimmed to 128 characters when a string. */
    value?: unknown;
  } | null;
}

/** Native signal block of an exported evidence row; each value is validated before use. */
interface EvidenceNativeSignals {
  /** Productivity investment, accepted on 0–10. */
  productivityInvestment?: unknown;
  /** Rejuvenation investment, accepted on 0–10. */
  rejuvenationInvestment?: unknown;
  /** Productivity return, accepted on 0–10. */
  productivityReturn?: unknown;
  /** Rejuvenation return, accepted on 0–10. */
  rejuvenationReturn?: unknown;
  /** Emotion label, accepted when a string. */
  emotion?: unknown;
  /** Local hour, accepted as an integer 0–23. */
  localHour?: unknown;
  /** Local weekday, accepted as an integer 0 (Sunday) through 6. */
  localDayOfWeek?: unknown;
}

/** One row of `observations` in the `/training/export` response, before validation. */
export interface EvidenceRow {
  /** Native signals; rows without them are skipped. */
  nativeSignals?: EvidenceNativeSignals | null;
  /** Camel-case team owner; any value marks the row team-scoped and skips it. */
  teamId?: unknown;
  /** Snake-case team owner; any value marks the row team-scoped and skips it. */
  team_id?: unknown;
  /** Evidence scope; `team` skips the row. */
  scope?: unknown;
  /** Bundle or project class; defaults to `personal Activity`. */
  activityClass?: unknown;
  /** Observed duration in minutes, accepted when finite. */
  actualDuration?: unknown;
  /** Bounded feature list; see `EvidenceFeature`. */
  features?: EvidenceFeature[];
}

/** Rows the evidence conversion accepts from the training export (at most 512). */
function evidenceRows(data: unknown): EvidenceRow[] {
  const observations = (data as { /** Exported evidence rows. */ observations?: unknown } | null | undefined)
    ?.observations;
  return Array.isArray(observations) ? (observations.slice(0, 512) as EvidenceRow[]) : [];
}

/** One converted, bounded model observation (the Rust `PersonalObservation` input shape). */
export interface PersonalObservation {
  /** Bundle/project class, trimmed to 128 characters. */
  activity_class: string;
  /** Up to 32 lowercase keyword features of the class. */
  title_terms: string[];
  /** Actual minutes, when finite. */
  actual_duration: number | null;
  /** Productivity investment on 0–10, or null when missing/out of range. */
  productivity_investment: number | null;
  /** Rejuvenation investment on 0–10, or null. */
  rejuvenation_investment: number | null;
  /** Productivity return on 0–10, or null. */
  productivity_return: number | null;
  /** Rejuvenation return on 0–10, or null. */
  rejuvenation_return: number | null;
  /** Emotion label up to 64 characters, or null. */
  emotion: string | null;
  /** Local hour 0–23, or null. */
  local_hour: number | null;
  /** Local weekday, Sunday = 0 through Saturday = 6, or null. */
  local_day_of_week: number | null;
  /** Up to 32 bounded personal features. */
  personal_features: unknown[];
}

/**
 * Convert one personal history record into a bounded model observation, or `undefined` when the
 * record is team-scoped, lacks native signals, or has no usable activity class. Rejects scores
 * outside 0–10, clamps numerical feature values, bounds text/features, and supplies local time
 * context.
 */
export function observationFromRow(row: EvidenceRow | null | undefined): PersonalObservation | undefined {
  if (!row?.nativeSignals || row.teamId || row.team_id || row.scope === 'team') return undefined;
  const native = row.nativeSignals;
  const activity_class = String(row.activityClass || 'personal Activity')
    .trim()
    .slice(0, 128);
  if (!activity_class) return undefined;
  return {
    activity_class,
    title_terms: terms(activity_class),
    actual_duration: finite(row.actualDuration),
    productivity_investment: score(native.productivityInvestment),
    rejuvenation_investment: score(native.rejuvenationInvestment),
    productivity_return: score(native.productivityReturn),
    rejuvenation_return: score(native.rejuvenationReturn),
    emotion: typeof native.emotion === 'string' ? native.emotion.slice(0, 64) : null,
    local_hour:
      typeof native.localHour === 'number' &&
      Number.isInteger(native.localHour) &&
      native.localHour >= 0 &&
      native.localHour <= 23
        ? native.localHour
        : null,
    local_day_of_week:
      typeof native.localDayOfWeek === 'number' &&
      Number.isInteger(native.localDayOfWeek) &&
      native.localDayOfWeek >= 0 &&
      native.localDayOfWeek <= 6
        ? native.localDayOfWeek
        : null,
    personal_features: (row.features || [])
      .filter(feature => feature.scope === 'personal' && !feature.teamId)
      .slice(0, 32)
      .flatMap(feature => {
        const weight = finite(feature.weight);
        const specificity = finite(feature.specificity);
        const value = feature.value;
        if (!feature.key || weight === null || specificity === null) return [];
        const numerical = value?.kind === 'numerical' ? finite(value.normalized) : null;
        let normalized;
        if (numerical !== null)
          normalized = {
            kind: 'numerical',
            normalized: Math.min(1, Math.max(0, numerical)),
          };
        else if (value?.kind === 'categorical' && typeof value.value === 'string')
          normalized = { kind: 'categorical', value: value.value.slice(0, 128) };
        else return [];
        return [
          {
            key: String(feature.key).slice(0, 128),
            specificity: Math.min(10, Math.max(0, specificity)),
            weight: Math.min(100, Math.max(0, weight)),
            value: normalized,
          },
        ];
      }),
  };
}

/**
 * Convert at most 512 personal history records with native signals into bounded model observations
 * in one synchronous pass. Excludes team scope; see `observationFromRow` for the per-row rules.
 */
export function personalObservations(data: unknown): PersonalObservation[] {
  return evidenceRows(data).flatMap(row => {
    const observation = observationFromRow(row);
    return observation ? [observation] : [];
  });
}

/**
 * Same result as `personalObservations`, computed in main-thread chunks of at most `budgetMs`
 * (default 8 ms) with a priority-preserving yield between them. Measured cost is about 3 ms for
 * 512 feature-rich rows on a desktop and 4-5x that on a mid-range phone, enough to become part of
 * a long task when it shares a turn with React rendering; chunking keeps it out of them. It stays
 * on the main thread because converting *trims* the export: posting raw rows to the Worker would
 * clone more data than posting the converted observations (see docs/developer/browser-compute.md).
 */
export function personalObservationsChunked(
  data: unknown,
  options: ChunkOptions = {}
): Promise<PersonalObservation[]> {
  return runChunked(evidenceRows(data), observationFromRow, {
    priority: 'user-blocking',
    ...options,
  }) as Promise<PersonalObservation[]>;
}
/**
 * Build a prediction target from an activity's bundle/project class and title terms, using the
 * current local hour and Sunday-zero weekday as context.
 */
export function activityTarget(task: TaskUI) {
  return activityTargetAt(task, new Date());
}
/**
 * Build a prediction target at an explicit local time. The weekday is Sunday = 0 through
 * Saturday = 6, matching captured observations (`localDayOfWeek`) and the shared schedule types.
 */
export function activityTargetAt(task: TaskUI, now: Date) {
  return {
    activity_id: task.id,
    activity_class: (task.bundleName || task.projectName || 'personal Activity').slice(0, 128),
    title_terms: terms(task.title),
    local_hour: now.getHours(),
    local_day_of_week: now.getDay(),
    personal_features: [],
  };
}
/**
 * Activity patch built by `predictionPatch`, sent through `PATCH /tasks/:id`. It always carries the
 * full merged state profile and custom attributes, so the write replaces both maps.
 */
export interface PredictionPatch {
  /** Existing P/RJ values with supported predictions filled into the missing ones. */
  state_profile: TaskStateProfile;
  /** Existing custom attributes plus prediction provenance and required-field bookkeeping. */
  custom_attributes: Record<string, unknown>;
  /** Predicted duration in whole minutes (1–1440), present only when the estimate was missing. */
  duration_minutes?: number;
}

// Confidence is an evidence-support score, not a probability of correctness.
/**
 * Minimum per-output evidence-support score (0.7) accepted for a predicted field; this is not a
 * calibrated probability.
 */
export const MIN_INFERENCE_CONFIDENCE = 0.7;
/**
 * Create a patch only for missing duration and P/RJ values supported by positive integer evidence
 * and confidence from 0.7 through 1. Duration must be an integer from 1 to 1440 minutes. Preserves
 * manual fields, does not infer emotion or resistance, and records provenance plus the need for
 * final server reranking.
 */
export function predictionPatch(
  task: TaskUI,
  prediction: InferenceBatch['predictions'][number],
  telemetry: InferenceBatch['telemetry']
): PredictionPatch | null {
  if (!Number.isInteger(prediction.evidence_count) || prediction.evidence_count < 1) return null;
  const supported = (index: number) => {
    const confidence = prediction.output_confidence?.[index];
    return (
      typeof confidence === 'number' &&
      Number.isFinite(confidence) &&
      confidence >= MIN_INFERENCE_CONFIDENCE &&
      confidence <= 1
    );
  };
  const state_profile = { ...task.stateProfile };
  const custom_attributes = { ...task.customAttributes };
  const predicted = new Set<string>(
    Array.isArray(custom_attributes.planning_fields_predicted)
      ? custom_attributes.planning_fields_predicted
      : []
  );
  const applied: string[] = [];
  const fields = [
    'required_productivity_energy',
    'required_rejuvenation_energy',
    'productivity_return',
    'rejuvenation_return',
  ] as const;
  fields.forEach((key, index) => {
    if (state_profile[key] == null && supported(index + 1) && score(prediction[key]) !== null) {
      state_profile[key] = prediction[key];
      predicted.add(key);
      applied.push(key);
    }
  });
  const patch: PredictionPatch = { state_profile, custom_attributes };
  // Existing values are preserved, including estimates a person may have edited.
  if (
    (task.estimatedDuration == null || task.estimatedDuration <= 0) &&
    supported(0) &&
    Number.isInteger(prediction.duration_minutes) &&
    prediction.duration_minutes > 0 &&
    prediction.duration_minutes <= 1440
  ) {
    patch.duration_minutes = prediction.duration_minutes;
    custom_attributes.duration_predicted = true;
    custom_attributes.duration_inferred = true;
    predicted.add('duration_minutes');
    applied.push('duration_minutes');
  }
  // The model has no per-output evidence for emotion or resistance. Leave them for the person.
  if (!applied.length) return null;
  const normalized = (field: string) =>
    field.trim().toLowerCase().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
  const aliases: Record<string, string[]> = {
    duration_minutes: ['duration', 'duration minutes'],
    required_productivity_energy: ['required p', 'required productivity energy'],
    required_rejuvenation_energy: ['required rj', 'required rejuvenation energy'],
    productivity_return: ['p return', 'productivity return'],
    rejuvenation_return: ['rj return', 'rejuvenation return'],
  };
  const supplied = new Set(applied.flatMap(field => aliases[field] || [normalized(field)]));
  if (Array.isArray(custom_attributes.missing_required_fields)) {
    const remaining = custom_attributes.missing_required_fields.filter(
      (field: string) => !supplied.has(normalized(field))
    );
    custom_attributes.missing_required_fields = remaining;
    custom_attributes.required_fields_complete = remaining.length === 0;
    custom_attributes.needs_review = remaining.length > 0;
  }
  custom_attributes.planning_fields_predicted = [...predicted];
  custom_attributes.planning_inference = {
    model: telemetry.model_version,
    feature_schema: telemetry.feature_schema_version,
    source: `browser_${telemetry.backend}`,
    confidence: prediction.confidence,
    evidence_count: prediction.evidence_count,
    output_confidence: prediction.output_confidence,
    applied_fields: applied,
    final_server_rerank_required: true,
  };
  return patch;
}
